import { DocumentInfo, DocumentType } from 'api';
import { uuid } from 'features';
import { useFormContext } from 'form';
import { useTranslation } from 'i18n';
import { IconButton, List, ListItem, ListItemIcon, ListItemText, Typography } from 'ui';
import { Delete, Folder } from 'ui/icons';

import type { UploadDocumentProps } from '../../types';
import { DOCUMENT_TYPE_DISPLAYED_VALUE } from '../../utils/document';

type Documents = {
  documents?: UploadDocumentProps[];
};

type Props = {
  name: keyof Documents;
  uploadedDocuments: DocumentInfo[];
};

export const DocumentList = ({ name, uploadedDocuments }: Props) => {
  const { t } = useTranslation('orders');
  const { watch, setValue } = useFormContext<Documents>();
  const documents = watch(name);
  const removeDocument = (index: number) => {
    const updatedDocuments = documents?.filter((_, i) => i !== index);
    setValue(name, updatedDocuments);
  };

  if (!documents?.length && !uploadedDocuments.length) {
    return null;
  }

  return (
    <>
      {documents && documents.length > 0 ? (
        <>
          <Typography variant="h6">{t('documents.list.toBeAdded')}</Typography>
          <List dense>
            {documents?.map((document, index: number) => (
              <ListItem
                key={uuid()}
                secondaryAction={
                  <IconButton
                    aria-label={t('documents.list.actions.deleteAriaLabel')}
                    onClick={() => removeDocument(index)}
                  >
                    <Delete color="error" />
                  </IconButton>
                }
                sx={{
                  alignItems: 'flex-start',
                }}
              >
                <ListItemIcon>
                  <Folder color="primary" />
                </ListItemIcon>
                <ListItemText primary={DOCUMENT_TYPE_DISPLAYED_VALUE[document.type]} secondary={document.file.name} />
              </ListItem>
            ))}
          </List>
        </>
      ) : null}
      {uploadedDocuments && uploadedDocuments.length > 0 ? (
        <>
          <Typography variant="h6">{t('documents.list.added')}</Typography>
          <List dense>
            {uploadedDocuments?.map((document) => (
              <ListItem
                key={document.id}
                sx={{
                  alignItems: 'flex-start',
                }}
              >
                <ListItemIcon>
                  <Folder color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={DOCUMENT_TYPE_DISPLAYED_VALUE[document.type as DocumentType]}
                  secondary={document.file_name}
                />
              </ListItem>
            ))}
          </List>
        </>
      ) : null}
    </>
  );
};
